import React from 'react';
import { Link } from 'react-router-dom';

export const TermsOfService = () => {
  return (
    <div className="w-full py-12 px-6">
      <div className="max-w-4xl mx-auto">
        {/* Back to Home Link */}
        <Link 
          to="/" 
          className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-black dark:hover:text-white transition-colors mb-8"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Home
        </Link>

        {/* Page Title */}
        <h1 className="text-4xl lg:text-5xl font-serif font-bold text-gray-900 dark:text-gray-100 mb-4 transition-colors">
          Terms of Service
        </h1>
        
        <p className="text-gray-600 dark:text-gray-400 mb-8 transition-colors">
          Last Updated: November 10, 2025
        </p>

        {/* Acceptance of Terms */}
        <section className="mb-8">
          <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-gray-100 mb-4 transition-colors">
            1. Acceptance of Terms
          </h2>
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-4 transition-colors">
            By accessing or using JABARI HOLDINGS LLC's ("ReelTravel," "we," "us," or "our") website, mobile application, or any related services (collectively, the "Services"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our Services.
          </p>
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed transition-colors">
            You must be at least 13 years old (or the age of majority in your jurisdiction) to use our Services. If you are using the Services on behalf of an organization, you represent that you have the authority to bind that organization to these Terms.
          </p>
        </section>

        {/* Description of Services */}
        <section className="mb-8">
          <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-gray-100 mb-4 transition-colors">
            2. Description of Services
          </h2>
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-4 transition-colors">
            ReelTravel provides tools for organizing, storing, and managing digital content, including AI-powered and personalized features designed to help users curate and rediscover online content. Certain features or functionalities may be offered through paid subscriptions ("Subscriptions").
          </p>
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed transition-colors">
            We may update or modify the Services at any time to improve performance, introduce new features, or comply with legal or technical requirements.
          </p>
        </section>

        {/* User Accounts */}
        <section className="mb-8">
          <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-gray-100 mb-4 transition-colors">
            3. User Accounts
          </h2>
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-4 transition-colors">
            To access certain features, you may need to create an account. You agree to:
          </p>
          <ul className="list-disc list-inside text-lg text-gray-700 dark:text-gray-300 leading-relaxed space-y-2 ml-4 transition-colors">
            <li>Provide accurate and complete information during registration.</li>
            <li>Maintain the confidentiality of your login credentials.</li>
            <li>Be responsible for all activities that occur under your account.</li>
            <li>Notify us immediately of any unauthorized use or security breach.</li>
          </ul>
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mt-4 transition-colors">
            ReelTravel will not be liable for any loss or damage arising from your failure to safeguard your account information.
          </p>
        </section>

        {/* Subscriptions and Payments */}
        <section className="mb-8">
          <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-gray-100 mb-4 transition-colors">
            4. Subscriptions and Payments
          </h2>
          
          <div className="mb-4">
            <h3 className="text-xl font-serif font-semibold text-gray-900 dark:text-gray-100 mb-3 transition-colors">
              4.1 Subscription Terms
            </h3>
            <ul className="list-disc list-inside text-lg text-gray-700 dark:text-gray-300 leading-relaxed space-y-2 ml-4 transition-colors">
              <li>Some parts of our Services require a paid Subscription, available on a recurring (auto-renewing) basis.</li>
              <li>If you purchase a Subscription through the Apple App Store, billing and payment are handled directly by Apple Inc. in accordance with their terms.</li>
              <li>Payment will be charged to your Apple ID account at confirmation of purchase.</li>
              <li>Your Subscription automatically renews unless auto-renew is turned off at least 24 hours before the end of the current billing period.</li>
              <li>Your Apple ID account will be charged for renewal within 24 hours before the end of the current period.</li>
              <li>You can manage or cancel your Subscription at any time in your Apple ID account settings.</li>
              <li>We do not have access to, or control over, Apple billing systems or payment information.</li>
            </ul>
          </div>

          <div className="mb-4">
            <h3 className="text-xl font-serif font-semibold text-gray-900 dark:text-gray-100 mb-3 transition-colors">
              4.2 Free Trials
            </h3>
            <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed transition-colors">
              If a free trial is offered, it will automatically convert to a paid Subscription unless cancelled before the end of the trial period. Only new users are eligible for free trials unless stated otherwise.
            </p>
          </div>

          <div className="mb-4">
            <h3 className="text-xl font-serif font-semibold text-gray-900 dark:text-gray-100 mb-3 transition-colors">
              4.3 Price Changes
            </h3>
            <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed transition-colors">
              We may change the price of Subscriptions at any time. If Apple requires, you will be notified before any price change and given the option to accept the new price or cancel before renewal.
            </p>
          </div>

          <div>
            <h3 className="text-xl font-serif font-semibold text-gray-900 dark:text-gray-100 mb-3 transition-colors">
              4.4 Refunds
            </h3>
            <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-2 transition-colors">
              All payments and refund requests for Subscriptions purchased through Apple are handled by Apple, not ReelTravel. To request a refund, please visit reportaproblem.apple.com.
            </p>
            <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed transition-colors">
              We cannot process refunds or manage billing disputes for transactions made through the App Store.
            </p>
          </div>
        </section>

        {/* User Content */}
        <section className="mb-8">
          <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-gray-100 mb-4 transition-colors">
            5. User Content
          </h2>
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-4 transition-colors">
            Our Services may allow you to store, share, or publish content ("User Content"). You retain ownership of your User Content, but grant ReelTravel a limited, worldwide, non-exclusive, royalty-free license to host, store, reproduce, and display it as necessary to provide the Services.
          </p>
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed transition-colors">
            You are solely responsible for your User Content and warrant that it does not infringe any third-party rights or violate applicable laws.
          </p>
        </section>

        {/* Prohibited Conduct */}
        <section className="mb-8">
          <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-gray-100 mb-4 transition-colors">
            6. Prohibited Conduct
          </h2>
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-4 transition-colors">
            You agree not to:
          </p>
          <ul className="list-disc list-inside text-lg text-gray-700 dark:text-gray-300 leading-relaxed space-y-2 ml-4 transition-colors">
            <li>Use our Services for any unlawful or unauthorized purpose.</li>
            <li>Infringe upon the rights of others.</li>
            <li>Upload or transmit viruses, malware, or harmful code.</li>
            <li>Attempt to gain unauthorized access to our systems.</li>
            <li>Interfere with or disrupt the normal operation of our Services.</li>
            <li>Use automated scripts, bots, or scrapers without prior written consent.</li>
          </ul>
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mt-4 transition-colors">
            We reserve the right to suspend or terminate accounts that violate these Terms.
          </p>
        </section>

        {/* Intellectual Property */}
        <section className="mb-8">
          <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-gray-100 mb-4 transition-colors">
            7. Intellectual Property
          </h2>
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed transition-colors">
            All rights, titles, and interests in the Services (including content, logos, trademarks, software, and designs) are owned by ReelTravel or its licensors. You may not copy, modify, distribute, sell, lease, reverse-engineer, or otherwise exploit any part of the Services without our prior written consent.
          </p>
        </section>

        {/* Termination */}
        <section className="mb-8">
          <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-gray-100 mb-4 transition-colors">
            8. Termination
          </h2>
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-4 transition-colors">
            We may suspend or terminate your access to the Services at any time, with or without notice, if you violate these Terms, create security or legal risks, or for other valid reasons.
          </p>
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed transition-colors">
            Upon termination, your right to use the Services will immediately cease. You may stop using the Services at any time by deleting your account or uninstalling the app.
          </p>
        </section>

        {/* Disclaimer of Warranties */}
        <section className="mb-8">
          <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-gray-100 mb-4 transition-colors">
            9. Disclaimer of Warranties
          </h2>
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-4 transition-colors">
            THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. TO THE MAXIMUM EXTENT PERMITTED BY LAW, REELTRAVEL DISCLAIMS ALL WARRANTIES, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
          </p>
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed transition-colors">
            We make no guarantees that the Services will be uninterrupted, secure, or error-free.
          </p>
        </section>

        {/* Limitation of Liability */}
        <section className="mb-8">
          <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-gray-100 mb-4 transition-colors">
            10. Limitation of Liability
          </h2>
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-4 transition-colors">
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, REELTRAVEL AND ITS AFFILIATES SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, CONSEQUENTIAL, SPECIAL, OR PUNITIVE DAMAGES ARISING FROM OR RELATED TO YOUR USE OF THE SERVICES.
          </p>
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed transition-colors">
            IN NO EVENT SHALL OUR TOTAL LIABILITY EXCEED THE AMOUNT YOU HAVE PAID TO US (IF ANY) IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
          </p>
        </section>

        {/* Changes to Terms */}
        <section className="mb-8">
          <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-gray-100 mb-4 transition-colors">
            11. Changes to Terms
          </h2>
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed transition-colors">
            We may update these Terms from time to time. If we make material changes, we will notify you via email, in-app notice, or on our website. Continued use of the Services after such updates constitutes your acceptance of the revised Terms.
          </p>
        </section>

        {/* Governing Law */}
        <section className="mb-8">
          <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-gray-100 mb-4 transition-colors">
            12. Governing Law
          </h2>
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed transition-colors">
            These Terms shall be governed by and construed in accordance with the laws of the Commonwealth of Kentucky and applicable federal laws of the United States, without regard to conflict of law provisions. You agree that the state and federal courts located in Kentucky shall have exclusive jurisdiction to resolve any dispute arising under these Terms.
          </p>
        </section>

        {/* Contact Information */}
        <section className="mb-8">
          <h2 className="text-2xl font-serif font-semibold text-gray-900 dark:text-gray-100 mb-4 transition-colors">
            13. Contact Information
          </h2>
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-4 transition-colors">
            If you have any questions about these Terms, please contact us at:
          </p>
          <div className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed transition-colors">
            <p className="font-semibold">JABARI HOLDINGS LLC</p>
            <p>Email: <a href="mailto:ferasjabari@hotmail.com" className="text-blue-600 dark:text-blue-400 hover:underline">ferasjabari@hotmail.com</a></p>
          </div>
        </section>
      </div>
    </div>
  );
};
